/** Typed fetch wrapper. No component calls `fetch` directly (doc 01 §2). */

import type { Problem } from './types'

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem | null
  readonly traceId: string | null

  constructor(status: number, problem: Problem | null, traceId: string | null) {
    super(problem?.detail || problem?.title || `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.traceId = traceId ?? problem?.trace_id ?? null
  }

  /** Field-level messages, for form error display. */
  get fieldErrors(): Array<{ field: string | null; code: string; message: string }> {
    return this.problem?.errors ?? []
  }
}

type QueryValue = string | number | boolean | null | undefined | Array<string | number>

export function buildQuery(params?: Record<string, QueryValue>): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item))
    } else {
      search.append(key, String(value))
    }
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal
  idempotencyKey?: string
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, idempotencyKey } = options
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  // Guards against a double-clicked "Run Evaluation" creating two runs.
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey

  const response = await fetch(`${API_BASE}${path}${buildQuery(query)}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
    credentials: 'include',
  })

  const traceId = response.headers.get('X-Trace-Id')

  if (response.status === 204) return undefined as T

  const contentType = response.headers.get('Content-Type') ?? ''
  const payload = contentType.includes('json') ? await response.json() : await response.text()

  if (!response.ok) {
    if (
      response.status === 401 &&
      typeof window !== 'undefined' &&
      !path.startsWith('/auth/') &&
      window.location.pathname !== '/login'
    ) {
      window.location.assign('/login')
    }
    throw new ApiError(
      response.status,
      typeof payload === 'object' ? (payload as Problem) : null,
      traceId,
    )
  }
  return payload as T
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<T>(path, { method: 'POST', body, idempotencyKey }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: (path: string) => request<void>(path, { method: 'DELETE' }),
  /** Multipart POST (model upload). Do not set Content-Type - the browser sets the boundary. */
  postForm: async <T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> => {
    const headers: Record<string, string> = { Accept: 'application/json' }
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      body: form,
      signal,
      credentials: 'include',
    })
    const traceId = response.headers.get('X-Trace-Id')
    const contentType = response.headers.get('Content-Type') ?? ''
    const payload = contentType.includes('json') ? await response.json() : await response.text()
    if (!response.ok) {
      throw new ApiError(
        response.status,
        typeof payload === 'object' ? (payload as Problem) : null,
        traceId,
      )
    }
    return payload as T
  },
}
